import { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { ScrollArea } from '@/components/ui/scroll-area';
import { downloadHubMethodologyMarkdown } from '@/lib/hubMethodologyDocument';

/* ─────────────── helpers ─────────────── */
const Box = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div className={`bg-muted rounded-lg p-3 border border-border ${className}`}>{children}</div>
);
const Formula = ({ children }: { children: React.ReactNode }) => (
  <code className="text-[9px] block mb-1 font-mono text-foreground/80">{children}</code>
);
const Callout = ({ type = 'info', children }: { type?: 'info' | 'warning' | 'tip'; children: React.ReactNode }) => {
  const styles = {
    info: 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-200',
    warning: 'bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800 text-orange-900 dark:text-orange-200',
    tip: 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800 text-green-900 dark:text-green-200',
  };
  const icons = { info: 'ℹ️', warning: '⚠️', tip: '💡' };
  return (
    <div className={`rounded-lg p-3 border text-[10px] leading-relaxed ${styles[type]}`}>
      <span className="mr-1">{icons[type]}</span>{children}
    </div>
  );
};
const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <div className="text-[10px] font-bold text-foreground mb-1">{children}</div>
);

/* ─────────────── TAB CONTENTS ─────────────── */

const OverzichtTab = () => (
  <Accordion type="multiple" className="w-full space-y-1">
    <AccordionItem value="wat">
      <AccordionTrigger className="text-[12px] font-semibold">Wat is de HUB Simulator?</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p>De HUB Simulator is een <strong>energiepark-simulatietool</strong> die berekent hoe zonne-energie (PV), batterijopslag (BESS), het elektriciteitsnet en backup-generatoren samenwerken om een vloot elektrische trucks op te laden.</p>
        <p>De tool combineert twee kernmodules:</p>
        <Box>
          <SectionTitle>1. Technische Simulatie</SectionTitle>
          <p className="text-[10px]">Draait een uur-voor-uur energiebalans over 8.760 uur (1 jaar). Berekent hoeveel energie uit elke bron komt, hoe de batterij zich gedraagt, en of er capaciteitstekorten ontstaan.</p>
        </Box>
        <Box>
          <SectionTitle>2. Financiële Business Case</SectionTitle>
          <p className="text-[10px]">Vertaalt de technische resultaten naar een DCF-analyse (Discounted Cash Flow) met NPV, IRR, LCOE, terugverdientijd, en gevoeligheidsanalyses.</p>
        </Box>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="voor-wie">
      <AccordionTrigger className="text-[12px] font-semibold">Voor wie is deze tool?</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <ul className="space-y-1.5 list-disc list-inside text-[10px]">
          <li><strong>Sales & Business Development</strong> — Maak snel indicatieve business cases voor potentiële klanten</li>
          <li><strong>Project Engineers</strong> — Dimensioneer PV, BESS en gridaansluiting op basis van echte vlootdata</li>
          <li><strong>Management</strong> — Vergelijk scenario's en beoordeel rendement en risico's</li>
          <li><strong>Klanten</strong> — Presenteer transparante, onderbouwde resultaten in PDF-formaat</li>
        </ul>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="workflow">
      <AccordionTrigger className="text-[12px] font-semibold">Hoe werkt de workflow?</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <ol className="space-y-2 list-decimal list-inside text-[10px]">
          <li><strong>Organisatie selecteren</strong> — Koppel de simulatie aan een klant uit het CRM</li>
          <li><strong>Nieuw scenario aanmaken</strong> — Geef een naam en start met default parameters</li>
          <li><strong>Technische parameters instellen</strong> — Stel PV-capaciteit, batterij, gridaansluiting en vloot in via de sliders</li>
          <li><strong>Simulatie bekijken</strong> — De simulatie draait automatisch en toont KPI's, Sankey-diagram, dagprofielen en maandoverzichten</li>
          <li><strong>Business Case berekenen</strong> — Schakel naar de BC-tab om financiële parameters in te vullen en de DCF-analyse te bekijken</li>
          <li><strong>Vergelijken & opslaan</strong> — Sla het scenario op, maak varianten, en vergelijk resultaten</li>
          <li><strong>Exporteren</strong> — Genereer een PDF-rapport voor interne of externe presentatie</li>
        </ol>
        <Callout type="tip">Gebruik de <strong>Undo/Redo</strong> knoppen (of Ctrl+Z / Ctrl+Shift+Z) om wijzigingen terug te draaien. Er worden maximaal 50 stappen onthouden.</Callout>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="outputs">
      <AccordionTrigger className="text-[12px] font-semibold">Wat levert de tool op?</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <div className="grid grid-cols-2 gap-2 text-[10px]">
          {[
            ['Energiebalans', 'Uur-voor-uur verdeling van PV, BESS, grid en backup'],
            ['Zelfvoorzieningsgraad', 'Percentage lokaal gedekte vraag'],
            ['KPI-dashboard', '12+ kerngetallen in overzichtelijke kaarten'],
            ['Sankey-diagram', 'Visuele energiestromen van bron naar bestemming'],
            ['Financieel model', 'NPV, IRR, LCOE, terugverdientijd, DSCR'],
            ['Risicoanalyse', 'Monte Carlo (1000 scenario\'s), gevoeligheid, break-even'],
            ['PDF-export', 'Professioneel rapport met alle grafieken en cijfers'],
            ['Versiebeheer', 'Opslaan, vergelijken en terugvallen op eerdere versies'],
          ].map(([title, desc], i) => (
            <Box key={i} className="py-2">
              <div className="font-bold text-foreground text-[10px]">{title}</div>
              <div className="text-[9px] text-muted-foreground">{desc}</div>
            </Box>
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
);

const ParametersTab = () => (
  <Accordion type="multiple" className="w-full space-y-1">
    <AccordionItem value="pv">
      <AccordionTrigger className="text-[12px] font-semibold">☀️ PV-systeem (Zonne-energie)</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p><strong>PV Capaciteit (MWp)</strong> — Het piekvermogen van het zonnepanelensysteem. 1 MWp levert in Nederland gemiddeld ~950 MWh/jaar op, afhankelijk van oriëntatie en schaduw.</p>
        <Box>
          <SectionTitle>Zonprofiel</SectionTitle>
          <p className="text-[10px]">Het model gebruikt een <strong>genormaliseerd Nederlands zonprofiel</strong> met uurwaarden per maand. Dit profiel is gebaseerd op KNMI-data en TNO-referentieprofielen. De productie varieert sterk per seizoen:</p>
          <table className="w-full text-[9px] mt-2">
            <tbody>
              {[['Dec-Jan', '~2-3% van jaarproductie/maand', 'Laagste opbrengst'], ['Mrt-Apr', '~8-10%', 'Snel stijgend'], ['Jun-Jul', '~13-15%', 'Piekmaanden'], ['Sep-Okt', '~6-8%', 'Dalend']].map(([m, p, n], i) => (
                <tr key={i} className="border-b border-border/50"><td className="py-1 font-semibold text-foreground w-24">{m}</td><td className="py-1">{p}</td><td className="py-1 text-muted-foreground">{n}</td></tr>
              ))}
            </tbody>
          </table>
        </Box>
        <Box>
          <SectionTitle>Berekening per uur</SectionTitle>
          <Formula>PV_productie[h] = PV_capaciteit × zonprofiel[maand][uur] × (1 − degradatie)^(jaar−1)</Formula>
          <p className="text-[10px]">De degradatie is standaard 0.5%/jaar voor PV-panelen (niet te verwarren met BESS-degradatie).</p>
        </Box>
        <Callout type="info">Een typisch dak van een logistiek centrum (10.000 m²) biedt ruimte voor ~1,0-1,5 MWp.</Callout>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="bess">
      <AccordionTrigger className="text-[12px] font-semibold">🔋 Batterij (BESS)</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-3 leading-relaxed">
        <p>Het Battery Energy Storage System (BESS) slaat overtollige PV-energie op voor later gebruik en fungeert als buffer tussen PV-productie en laadvraag.</p>
        <Box>
          <SectionTitle>Capaciteit (MWh)</SectionTitle>
          <p className="text-[10px]">De totale opslagcapaciteit in MWh. Dit bepaalt hoeveel energie de batterij maximaal kan bevatten. Let op: de <em>bruikbare</em> capaciteit is lager door DoD-begrenzing.</p>
          <Formula>Bruikbare capaciteit = Nominaal × DoD × (1 − degradatie%)^(jaar−1)</Formula>
        </Box>
        <Box>
          <SectionTitle>Vermogen (MW)</SectionTitle>
          <p className="text-[10px]">Het maximale laad- en ontlaadvermogen. Bepaalt hoe snel de batterij kan laden of ontladen. Een 2 MWh batterij met 1 MW vermogen is een C-rate van 0.5C (2 uur voor volledig laden).</p>
          <Formula>C-rate = Vermogen (MW) / Capaciteit (MWh)</Formula>
        </Box>
        <Box>
          <SectionTitle>Rendement (η)</SectionTitle>
          <p className="text-[10px]">Het round-trip rendement geeft aan hoeveel energie er overblijft na een volledig laad-ontlaadcyclus. Default: <strong>90%</strong>.</p>
          <p className="text-[10px] mt-1">In het model wordt het rendement verdeeld over laden en ontladen via de vierkantswortel:</p>
          <Formula>η_laden = √η = √0.90 ≈ 0.9487 (94.87%)</Formula>
          <Formula>η_ontladen = √η = √0.90 ≈ 0.9487 (94.87%)</Formula>
          <Formula>Round-trip = η_laden × η_ontladen = 0.9487² = 0.90 (90%)</Formula>
        </Box>
        <Box>
          <SectionTitle>Depth of Discharge (DoD)</SectionTitle>
          <p className="text-[10px]">Het percentage van de totale capaciteit dat daadwerkelijk gebruikt mag worden. Default: <strong>90%</strong>. Een DoD van 90% bij een 2 MWh batterij betekent dat er 1,8 MWh bruikbaar is. De resterende 10% beschermt de levensduur van de batterij.</p>
        </Box>
        <Box>
          <SectionTitle>Degradatie</SectionTitle>
          <p className="text-[10px]">Batterijen verliezen elk jaar een klein percentage van hun capaciteit. Default: <strong>2% per jaar</strong>.</p>
          <Formula>Effectieve capaciteit jaar 5 = 2 MWh × (1 − 0.02)⁴ = 2 × 0.922 = 1.844 MWh</Formula>
          <p className="text-[10px]">Het projectjaar-instelling bepaalt welk degradatieniveau wordt toegepast in de simulatie.</p>
        </Box>
        <Callout type="warning">Bij een te kleine batterij ten opzichte van PV-capaciteit ontstaat curtailment (verloren zonne-energie). Bij te weinig vermogen wordt de laad/ontlaadsnelheid de bottleneck.</Callout>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="grid">
      <AccordionTrigger className="text-[12px] font-semibold">🔌 Gridaansluiting</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p><strong>Aansluitcapaciteit (MW)</strong> — De maximale import- én exportcapaciteit van de netaansluiting. Dit is de fysieke begrenzing van hoeveel stroom er per uur van of naar het net kan.</p>
        <Box>
          <SectionTitle>Hoe het werkt in de simulatie</SectionTitle>
          <ul className="text-[10px] space-y-1 list-disc list-inside">
            <li><strong>Import</strong>: Als PV + BESS onvoldoende is, wordt stroom geïmporteerd tot max gridcapaciteit</li>
            <li><strong>Export</strong>: Als er PV-overschot is na BESS-laden, wordt geëxporteerd tot max gridcapaciteit</li>
            <li><strong>Congestiemanagement</strong>: In de praktijk kan het net op piekmomenten vol zitten. Het model gaat uit van constante beschikbaarheid maar begrenst op de ingestelde capaciteit</li>
          </ul>
        </Box>
        <Callout type="info">Een grotere gridaansluiting is duurder (CAPEX) maar biedt meer flexibiliteit. Een kleinere aansluiting kan leiden tot meer curtailment of ongedekte vraag — maar bespaart op aansluitkosten.</Callout>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="blokstroom">
      <AccordionTrigger className="text-[12px] font-semibold">🌙 Blokstroom</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p><strong>Blokstroom</strong> is een noodoplossing van netbeheerders om extra capaciteit te bieden buiten piekuren (typisch 's nachts). Het is een <strong>apart kanaal</strong> dat volledig los staat van de normale gridaansluiting (gMax).</p>
        <Box>
          <SectionTitle>Hoe het verschilt van grid</SectionTitle>
          <ul className="text-[10px] space-y-1 list-disc list-inside">
            <li><strong>Onafhankelijk van gMax</strong> — Bij gMax=0.2 MW en blokstroom=3 MW is de totale importcapaciteit 3.2 MW tijdens blokstroom-uren</li>
            <li><strong>Tijdgebonden</strong> — Alleen beschikbaar binnen de ingestelde uren (bijv. 00:00-06:00)</li>
            <li><strong>Eigen tarief</strong> — Blokstroom-import wordt apart getrackt en afgerekend tegen een blokstroomtarief (default €0.08/kWh)</li>
          </ul>
        </Box>
        <Box>
          <SectionTitle>Proactief laden</SectionTitle>
          <p className="text-[10px]">Tijdens blokstroom-uren wordt de batterij <strong>proactief</strong> opgeladen, ongeacht de actuele vraag. Dit is de kernstrategie: goedkoop laden 's nachts, overdag ontladen bij piekbelasting.</p>
          <Formula>blokCharge = min(blokstroom_vermogen, resterende_laadcapaciteit_BESS, beschikbare_SOC_ruimte)</Formula>
        </Box>
        <Box>
          <SectionTitle>Parameters</SectionTitle>
          <ul className="text-[10px] space-y-1 list-disc list-inside">
            <li><strong>Vermogen (MW)</strong> — Maximaal importvermogen via blokstroom</li>
            <li><strong>Start/eind uur</strong> — Tijdvenster waarin blokstroom actief is (ondersteunt overnachting, bijv. 22:00-06:00)</li>
          </ul>
        </Box>
        <Callout type="tip">Blokstroom is vooral effectief bij sites met hoge nacht-laadvraag en beperkte gridcapaciteit. Het verlaagt de afhankelijkheid van dure piek-import overdag.</Callout>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="backup">
      <AccordionTrigger className="text-[12px] font-semibold">⛽ Backup Generator</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p><strong>Vermogen (MW)</strong> — De capaciteit van de dieselgenerator die als laatste redmiddel wordt ingezet wanneer PV, BESS én grid onvoldoende zijn.</p>
        <Box>
          <SectionTitle>Wanneer wordt de backup ingezet?</SectionTitle>
          <p className="text-[10px]">Pas als stap 4 in de merit-order: PV → BESS → Grid → <strong>Backup</strong>. De generator draait alleen als alle andere bronnen uitgeput zijn. Dit minimaliseerde dieselverbruik en CO₂-uitstoot.</p>
        </Box>
        <Box>
          <SectionTitle>Kosten in de Business Case</SectionTitle>
          <ul className="text-[10px] space-y-1 list-disc list-inside">
            <li>CAPEX: €800/kW (éénmalige aanschaf)</li>
            <li>Brandstof: €1.50/liter diesel, verbruik ~0.3 l/kWh</li>
            <li>Onderhoud: inbegrepen in O&M-kosten</li>
          </ul>
        </Box>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="vloot">
      <AccordionTrigger className="text-[12px] font-semibold">🚛 Vlootconfiguratie</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p>De laadvraag wordt bepaald door het aantal trucks, hun energieverbruik en de laadpatronen. Het siteprofiel definieert het gemiddelde 24-uurs laadprofiel.</p>
        <Box>
          <SectionTitle>Laadprofiel</SectionTitle>
          <p className="text-[10px]">Elk siteprofiel bevat een genormaliseerd 24-uurs laadpatroon dat de relatieve vraag per uur aangeeft. Typisch patroon voor logistiek:</p>
          <ul className="text-[10px] space-y-1 list-disc list-inside mt-1">
            <li><strong>Nachtladen (22:00-06:00)</strong>: 60-80% van de vraag — trucks staan in de nacht</li>
            <li><strong>Bijladen (10:00-14:00)</strong>: 10-20% — tussenstops overdag</li>
            <li><strong>Piekvraag (17:00-20:00)</strong>: 15-25% — retour van routes</li>
          </ul>
        </Box>
        <Box>
          <SectionTitle>Seizoensfactoren</SectionTitle>
          <p className="text-[10px]">Energieverbruik varieert per seizoen door temperatuur (cabineverwarming/-koeling) en rijomstandigheden:</p>
          <table className="w-full text-[9px] mt-1">
            <tbody>
              {[['Winter (Dec-Feb)', '1.10-1.15×', 'Hoger door verwarming'], ['Lente (Mrt-Mei)', '1.00×', 'Referentie'], ['Zomer (Jun-Aug)', '1.02-1.05×', 'Licht hoger door koeling'], ['Herfst (Sep-Nov)', '1.03-1.05×', 'Licht hoger']].map(([s, f, n], i) => (
                <tr key={i} className="border-b border-border/50"><td className="py-1 font-semibold text-foreground">{s}</td><td className="py-1 text-right">{f}</td><td className="py-1 text-muted-foreground pl-2">{n}</td></tr>
              ))}
            </tbody>
          </table>
        </Box>
        <Box>
          <SectionTitle>Demandcijfer</SectionTitle>
          <p className="text-[10px]">De jaarlijkse energievraag in MWh, berekend op basis van het siteprofiel. Dit is het basisgetal dat met seizoensfactoren en het 24-uurs profiel wordt vermenigvuldigd.</p>
          <Formula>Vraag[h] = Demandcijfer × profiel[h] × seizoensfactor[maand]</Formula>
        </Box>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="projyear">
      <AccordionTrigger className="text-[12px] font-semibold">📅 Projectjaar</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p>Het projectjaar bepaalt in welk jaar van de levensduur de simulatie draait. Dit beïnvloedt uitsluitend de <strong>BESS-degradatie</strong>:</p>
        <Box>
          <Formula>Effectieve BESS = Nominaal × DoD × (1 − degradatie%)^(projectjaar − 1)</Formula>
          <table className="w-full text-[9px] mt-2">
            <thead><tr className="text-muted-foreground"><th className="text-left py-1">Jaar</th><th className="text-right py-1">1000 kWh × 90% DoD</th><th className="text-right py-1">Effectief</th></tr></thead>
            <tbody>
              {[[1, 900], [5, 830], [10, 751], [15, 680], [20, 616]].map(([y, eff]) => (
                <tr key={y} className="border-t border-border/50"><td className="py-1">Jaar {y}</td><td className="text-right">{(1000 * 0.9 * Math.pow(0.98, (y as number) - 1)).toFixed(0)} kWh</td><td className="text-right font-semibold text-foreground">{eff} kWh</td></tr>
              ))}
            </tbody>
          </table>
        </Box>
        <Callout type="tip">Gebruik projectjaar 1 voor de initiële dimensionering. Draai daarna jaar 10 en 15 om te zien hoe het systeem presteert met verouderde batterijen.</Callout>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
);

const SimulatieTab = () => (
  <Accordion type="multiple" className="w-full space-y-1">
    <AccordionItem value="overview">
      <AccordionTrigger className="text-[12px] font-semibold">🕐 Uur-voor-uur simulatie (8.760 uur)</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p>De simulatie doorloopt <strong>elk uur van het jaar</strong> (365 dagen × 24 uur = 8.760 iteraties). Per uur worden de volgende stappen doorlopen:</p>
        <Box>
          <ol className="text-[10px] space-y-1.5 list-decimal list-inside">
            <li><strong>Bereken PV-productie</strong> — Op basis van capaciteit × zonprofiel[maand][uur]</li>
            <li><strong>Bereken laadvraag</strong> — Op basis van vlootprofiel × seizoensfactor × demandcijfer</li>
            <li><strong>Bereken netto</strong> — PV productie − Laadvraag = overschot of tekort</li>
            <li><strong>Dispatch</strong> — Verdeel energie volgens merit-order (zie hieronder)</li>
            <li><strong>Update BESS SOC</strong> — Pas state-of-charge aan op basis van laden/ontladen</li>
            <li><strong>Registreer</strong> — Log alle waarden voor grafieken en KPI-berekeningen</li>
          </ol>
        </Box>
        <Callout type="info">De simulatie draait volledig in de browser — er is geen server nodig. Bij wijziging van een parameter wordt de simulatie automatisch opnieuw uitgevoerd.</Callout>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="dispatch">
      <AccordionTrigger className="text-[12px] font-semibold">⚡ Dispatch-algoritme (Merit Order)</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-3 leading-relaxed">
        <p>Het dispatch-algoritme bepaalt welke energiebron wordt ingezet. Het volgt een <strong>merit-order</strong> principe: de goedkoopste en schoonste bron eerst.</p>
        
        <Box>
          <SectionTitle>☀️ Scenario A: PV Overschot (productie ≥ vraag)</SectionTitle>
          <ol className="text-[10px] space-y-1.5 list-decimal list-inside mt-1">
            <li><strong>Direct verbruik</strong> — Alle laadvraag wordt direct uit PV gedekt (gratis, geen verlies)</li>
            <li><strong>BESS laden</strong> — Overschot gaat naar de batterij tot deze vol is of het maximale laadvermogen is bereikt
              <br/><span className="text-[9px] text-muted-foreground ml-4">→ Opgeslagen = min(overschot, max_laadvermogen, beschikbare_ruimte) × √η</span></li>
            <li><strong>Grid export</strong> — Resterend overschot wordt geëxporteerd tot max aansluitcapaciteit
              <br/><span className="text-[9px] text-muted-foreground ml-4">→ Opbrengst: exportprijs × geëxporteerde kWh</span></li>
            <li><strong>Curtailment</strong> — Alles wat overblijft gaat verloren (geen opslag, geen export mogelijk)</li>
          </ol>
        </Box>

        <Box>
          <SectionTitle>🌙 Scenario B: PV Tekort (productie &lt; vraag)</SectionTitle>
          <ol className="text-[10px] space-y-1.5 list-decimal list-inside mt-1">
            <li><strong>PV direct</strong> — Alle beschikbare PV wordt direct benut</li>
            <li><strong>BESS ontladen</strong> — Batterij levert energie tot deze leeg is (SOC = min) of max ontlaadvermogen
              <br/><span className="text-[9px] text-muted-foreground ml-4">→ Geleverd = min(tekort, max_ontlaadvermogen, beschikbare_energie) × √η</span></li>
            <li><strong>Grid import (+ blokstroom)</strong> — Rest wordt van het net gehaald tot max aansluitcapaciteit. Tijdens blokstroom-uren wordt de totale importcap verhoogd met het blokstroomvermogen
              <br/><span className="text-[9px] text-muted-foreground ml-4">→ Kosten: importprijs × normale import + blokstroomtarief × blokstroom-import</span></li>
            <li><strong>Backup generator</strong> — Alleen als grid ook niet voldoende is
              <br/><span className="text-[9px] text-muted-foreground ml-4">→ Kosten: brandstofprijs × verbruik per MWh</span></li>
            <li><strong>Ongedekte vraag</strong> — Als alles uitgeput is = capaciteitstekort (signaal om te op-dimensioneren)</li>
          </ol>
        </Box>

        <Box>
          <SectionTitle>🌙 Proactief blokstroom laden</SectionTitle>
          <p className="text-[10px]">Na de standaard dispatch wordt tijdens blokstroom-uren een extra stap uitgevoerd: de BESS wordt proactief opgeladen via blokstroom, <strong>onafhankelijk van de actuele laadvraag</strong> en <strong>onafhankelijk van gMax</strong>.</p>
          <Formula>blokCharge = min(blokstroom_vermogen, resterende_BESS_laadcapaciteit, SOC_ruimte / √η)</Formula>
          <p className="text-[10px] mt-1">Dit laadt de batterij 's nachts goedkoop op zodat overdag minder dure grid-import nodig is.</p>
        </Box>

        <Callout type="warning">Een hoog percentage ongedekte vraag duidt op onderdimensionering. Verhoog gridcapaciteit, BESS-vermogen of backup om dit op te lossen.</Callout>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="soc">
      <AccordionTrigger className="text-[12px] font-semibold">🔄 SOC-tracking & Round-Trip Efficiency</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p>De <strong>State of Charge (SOC)</strong> wordt elk uur bijgewerkt. De batterij start standaard op 50% SOC.</p>
        <Box>
          <SectionTitle>SOC-berekening per uur</SectionTitle>
          <Formula>Bij laden: SOC_nieuw = SOC_oud + (geladen_kWh × √η) / effectieve_capaciteit</Formula>
          <Formula>Bij ontladen: SOC_nieuw = SOC_oud − (ontladen_kWh / √η) / effectieve_capaciteit</Formula>
          <Formula>Begrenzing: SOC_min = (1 − DoD) → bijv. 0.10 bij 90% DoD</Formula>
          <Formula>Begrenzing: SOC_max = 1.00 (100%)</Formula>
        </Box>
        <Box>
          <SectionTitle>Waarom √η?</SectionTitle>
          <p className="text-[10px]">Het round-trip rendement (bijv. 90%) verdeelt het verlies gelijk over laden en ontladen. Als je 100 kWh inlaadt bij η=90%, sla je 94.87 kWh op (√0.90 = 0.9487). Bij ontladen lever je 94.87 × 0.9487 = 90 kWh. Totaal: 90% round-trip.</p>
        </Box>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="formulas">
      <AccordionTrigger className="text-[12px] font-semibold">📐 Kernformules</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <Box>
          <SectionTitle>Energiebalans (per uur)</SectionTitle>
          <Formula>Netto = PV_productie − Laadvraag</Formula>
          <Formula>Direct_verbruik = min(PV_productie, Laadvraag)</Formula>
        </Box>
        <Box>
          <SectionTitle>Jaarlijkse KPI's</SectionTitle>
          <Formula>Zelfvoorzieningsgraad = (Σ PV_direct + Σ BESS_ontladen) / Σ Laadvraag × 100%</Formula>
          <Formula>PV-benutting = (Σ PV_direct + Σ BESS_laden) / Σ PV_productie × 100%</Formula>
          <Formula>Curtailment% = Σ Curtailment / Σ PV_productie × 100%</Formula>
          <Formula>Backup-aandeel = Σ Backup / Σ Laadvraag × 100%</Formula>
        </Box>
        <Box>
          <SectionTitle>CO₂-reductie</SectionTitle>
          <Formula>Vermeden_CO₂ = Σ Laadvraag × (diesel_factor − grid_factor)</Formula>
          <Formula>diesel_factor = 2.68 kg CO₂/liter ÷ ~3.3 kWh/liter ≈ 0.81 kg/kWh equivalent</Formula>
          <Formula>grid_factor = 0.4 kg CO₂/kWh (NL gridemissie-intensiteit 2024)</Formula>
        </Box>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
);

const ResultatenTab = () => (
  <Accordion type="multiple" className="w-full space-y-1">
    <AccordionItem value="kpis">
      <AccordionTrigger className="text-[12px] font-semibold">📊 KPI-kaarten</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p>Na elke simulatie worden de volgende KPI's berekend en getoond als kaarten bovenaan het dashboard:</p>
        <div className="space-y-1.5">
          {[
            ['PV Productie', 'Totale jaarlijkse PV-opbrengst in MWh', 'Hoger = meer zonne-energie beschikbaar'],
            ['Zelfvoorzieningsgraad', 'Percentage laadvraag lokaal gedekt (PV + BESS)', 'Doel: >60% voor een goed energiepark'],
            ['PV Direct Verbruik', 'kWh PV direct benut zonder opslag', 'Hogere waarde = betere match PV-vraag'],
            ['BESS Cycli', 'Aantal equivalente volledige laad-ontlaadcycli per jaar', 'Typisch: 200-400 cycli/jaar'],
            ['Grid Import', 'Totale import van het net in MWh', 'Lager = minder afhankelijk van net'],
            ['Grid Export', 'Totale export naar het net in MWh', 'Opbrengst, maar minder dan direct verbruik'],
            ['Curtailment', 'Verloren PV-energie (niet opgeslagen, niet geëxporteerd)', 'Houd <5% — anders grotere BESS/grid'],
            ['Backup Gebruik', 'kWh uit dieselgenerator', 'Minimaliseer — duurste en vuilste bron'],
            ['Piekbelasting', 'Maximale gelijktijdige vraag in kW', 'Bepaalt minimale gridaansluiting'],
            ['CO₂-reductie', 'Vermeden tonnen CO₂ t.o.v. diesel', 'Sterk verkoopargument'],
          ].map(([name, desc, hint], i) => (
            <Box key={i} className="py-2">
              <div className="font-bold text-foreground text-[10px]">{name}</div>
              <div className="text-[9px]">{desc}</div>
              <div className="text-[9px] text-primary/70 mt-0.5">💡 {hint}</div>
            </Box>
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="sankey">
      <AccordionTrigger className="text-[12px] font-semibold">🔀 Sankey Energiestromen</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p>Het Sankey-diagram toont alle energiestromen als proportionele banden van <strong>bron</strong> (links) naar <strong>bestemming</strong> (rechts).</p>
        <Box>
          <SectionTitle>Bronnen (links)</SectionTitle>
          <ul className="text-[10px] space-y-1 list-disc list-inside">
            <li><strong>PV</strong> — Totale jaarproductie</li>
            <li><strong>Grid Import</strong> — Stroom van het net (incl. blokstroom-aandeel als apart zichtbaar)</li>
            <li><strong>Backup</strong> — Dieselgenerator</li>
          </ul>
          <p className="text-[10px] mt-1">Wanneer blokstroom actief is, wordt het blokstroom-volume apart weergegeven in de leveringsmix met een paarse indicator (🌙). De Sankey toont een aparte flow van Grid naar BESS voor proactief blokstroom-laden.</p>
        </Box>
        <Box>
          <SectionTitle>Bestemmingen (rechts)</SectionTitle>
          <ul className="text-[10px] space-y-1 list-disc list-inside">
            <li><strong>Direct Laden</strong> — PV direct naar trucks</li>
            <li><strong>BESS → Laden</strong> — Via batterij naar trucks</li>
            <li><strong>Grid Export</strong> — Overschot verkocht aan net</li>
            <li><strong>Curtailment</strong> — Verloren energie</li>
            <li><strong>BESS Verlies</strong> — Round-trip rendementsverlies</li>
          </ul>
        </Box>
        <Callout type="tip">Hoe dunner de curtailment-band, hoe beter het systeem gedimensioneerd is. Een dikke grid-import band duidt op onderdimensionering van PV/BESS.</Callout>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="dagprofiel">
      <AccordionTrigger className="text-[12px] font-semibold">📈 Dagprofiel (24-uurs)</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p>Toont het <strong>gemiddelde uurlijkse verloop</strong> voor een geselecteerde maand. Elke lijn/area vertegenwoordigt een energiebron.</p>
        <Box>
          <SectionTitle>Hoe te lezen</SectionTitle>
          <ul className="text-[10px] space-y-1 list-disc list-inside">
            <li><strong>Gele area</strong> — PV-productie (piek rond 12:00-14:00)</li>
            <li><strong>Blauwe lijn</strong> — Laadvraag (volgt vlootprofiel)</li>
            <li><strong>Groene area</strong> — BESS ontladen (typisch avond/nacht)</li>
            <li><strong>Rode area</strong> — Grid import (wanneer BESS leeg is)</li>
            <li><strong>Grijze area</strong> — Backup (laatste redmiddel)</li>
          </ul>
        </Box>
        <p>Gebruik de maandselector om seizoenseffecten te analyseren: in december is de PV-piek veel lager en korter dan in juni.</p>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="maand">
      <AccordionTrigger className="text-[12px] font-semibold">📊 Maandoverzicht</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p>Gestapelde bars per maand die tonen welk percentage van de laadvraag door welke bron wordt gedekt.</p>
        <Box>
          <SectionTitle>Typisch patroon</SectionTitle>
          <ul className="text-[10px] space-y-1 list-disc list-inside">
            <li><strong>Zomer (Jun-Aug)</strong>: Hoog PV-aandeel, weinig grid import, mogelijk curtailment</li>
            <li><strong>Winter (Dec-Feb)</strong>: Laag PV-aandeel, hoog grid import, mogelijk backup</li>
            <li><strong>Lente/Herfst</strong>: Tussenliggend, goede BESS-benutting</li>
          </ul>
        </Box>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
);

const BusinessCaseTab = () => (
  <Accordion type="multiple" className="w-full space-y-1">
    <AccordionItem value="capex">
      <AccordionTrigger className="text-[12px] font-semibold">🏗️ CAPEX (Investeringskosten)</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p>De totale investering (CAPEX) wordt opgebouwd uit de volgende componenten:</p>
        <table className="w-full text-[10px] border-collapse">
          <thead><tr className="bg-muted"><th className="text-left px-2 py-1.5 border-b border-border">Component</th><th className="text-right px-2 py-1.5 border-b border-border">Eenheid</th><th className="text-left px-2 py-1.5 border-b border-border">Berekening</th></tr></thead>
          <tbody>
            {[
              ['PV-systeem', '€/kWp', 'PV_capaciteit × €550'],
              ['BESS', '€/kWh', 'BESS_capaciteit × €350'],
              ['Laadinfrastructuur', '€/truck', 'Aantal_trucks × €45.000'],
              ['Backup generator', '€/kW', 'Backup_vermogen × €800'],
              ['Netaansluiting', 'vast', '€500.000 (configureerbaar)'],
              ['Engineering', '%', '10% over totaal bovenstaande'],
            ].map(([comp, unit, calc], i) => (
              <tr key={i} className="border-b border-border/50"><td className="px-2 py-1 font-semibold text-foreground">{comp}</td><td className="px-2 py-1 text-right">{unit}</td><td className="px-2 py-1 text-muted-foreground">{calc}</td></tr>
            ))}
          </tbody>
        </table>
        <Formula>Totaal CAPEX = (PV + BESS + Laadinfra + Backup + Grid) × (1 + Engineering%)</Formula>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="cashflow">
      <AccordionTrigger className="text-[12px] font-semibold">💰 Cashflow-model</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p>Het jaarlijkse cashflow-model berekent voor elk jaar van de projectduur (standaard 20 jaar):</p>
        <Box>
          <SectionTitle>Inkomsten</SectionTitle>
          <ul className="text-[10px] space-y-1 list-disc list-inside">
            <li><strong>Laadopbrengsten</strong> — Laadvraag × laadtarief × escalatiefactor — de primaire inkomstenbron</li>
            <li><strong>Grid export</strong> — Geëxporteerde kWh × exportprijs × energieprijsfactor</li>
            <li><strong>SDE++ subsidie</strong> — PV-productie × SDE-tarief (eerste 15 jaar, niet geïndexeerd)</li>
            <li><strong>ERE (Energie Reductie Eis)</strong> — Laadvraag × ERE-tarief × escalatiefactor (default €0.13/kWh)</li>
          </ul>
        </Box>
        <Box>
          <SectionTitle>Kosten (OPEX)</SectionTitle>
          <ul className="text-[10px] space-y-1 list-disc list-inside">
            <li><strong>Grid import (regulier)</strong> — Reguliere import × importtarief × energieprijsfactor (excl. blokstroom)</li>
            <li><strong>Blokstroom-import</strong> — Import blokstroom-uren × blokstroomtarief × energieprijsfactor (default €0.08/kWh)</li>
            <li><strong>Netbeheerdertarieven</strong> — (gMax_kW × transporttarief + systeemdienstenkosten + meetkosten) × inflatiefactor</li>
            <li><strong>Backup brandstof</strong> — Backup MWh × brandstofkosten × inflatiefactor</li>
            <li><strong>O&M PV</strong> — PV CAPEX × 1.0% × inflatiefactor</li>
            <li><strong>O&M BESS</strong> — BESS kWh × €5/kWh/jaar × inflatiefactor</li>
            <li><strong>Verzekering</strong> — Totaal CAPEX × 0.5% × inflatiefactor</li>
            <li><strong>Huur locatie</strong> — m² × €/m²/jaar × inflatiefactor</li>
            <li><strong>Overige kosten</strong> — Vrij configureerbare jaarlijkse posten × inflatiefactor</li>
            <li><strong>BESS vervanging</strong> — Eenmalig in jaar 12: BESS CAPEX × (1 − 30%)</li>
            <li><strong>Schuldaflossing</strong> — Annuïteit op vreemd vermogen (70% × CAPEX)</li>
            <li><strong>VPB</strong> — 25.8% over fiscale winst (EBITDA − afschrijving)</li>
          </ul>
        </Box>
        <Box>
          <SectionTitle>Netto cashflow</SectionTitle>
          <Formula>EBITDA = Totaal inkomsten − Totaal OPEX</Formula>
          <Formula>Netto_CF = EBITDA − Schuldaflossing − VPB + Fiscaal voordeel (EIA/MIA)</Formula>
        </Box>
        <Box>
          <SectionTitle>Inflatie- en prijscorrectie</SectionTitle>
          <p className="text-[10px]">Alle inkomsten en kosten worden jaarlijks geïndexeerd. Schuldaflossingen zijn nominaal (niet geïndexeerd).</p>
          <Formula>Inflatiefactor = (1 + inflatie%)^(jaar−1)</Formula>
          <Formula>Escalatiefactor = (1 + tariefescalatie%)^(jaar−1)</Formula>
          <Formula>Energieprijsfactor = non-lineaire functie (rekening houdend met prijsvolatiliteit)</Formula>
        </Box>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="dcf">
      <AccordionTrigger className="text-[12px] font-semibold">📐 DCF-methode (NPV, IRR, WACC)</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p>De <strong>Discounted Cash Flow</strong> analyse berekent de huidige waarde van alle toekomstige kasstromen.</p>
        <Box>
          <SectionTitle>NPV (Net Present Value)</SectionTitle>
          <Formula>NPV = Σ(CFₜ / (1 + WACC)ᵗ) voor t = 0 tot N</Formula>
          <p className="text-[10px]"><strong>Positief</strong> = het project verdient meer dan de vermogenskosten → waardevol. <strong>Negatief</strong> = het project vernietigt waarde.</p>
        </Box>
        <Box>
          <SectionTitle>IRR (Internal Rate of Return)</SectionTitle>
          <Formula>IRR = de discontovoet waarbij NPV exact 0 is</Formula>
          <p className="text-[10px]">Vergelijk IRR met WACC: <strong>IRR &gt; WACC</strong> = project loont. Hoe groter het verschil, hoe aantrekkelijker.</p>
        </Box>
        <Box>
          <SectionTitle>WACC (Weighted Average Cost of Capital)</SectionTitle>
          <Formula>WACC = EV% × Ke + VV% × Kd × (1 − VPB%)</Formula>
          <p className="text-[10px]">Waarin: EV% = aandeel eigen vermogen, Ke = kosten eigen vermogen, VV% = aandeel vreemd vermogen, Kd = rente op schuld. Default: <strong>6%</strong> (marktconform voor renewables).</p>
        </Box>
        <Callout type="info">In mensentaal: WACC is het minimale rendement dat investeerders eisen. NPV is hoeveel het project méér oplevert dan dat minimum. IRR is het werkelijke rendement.</Callout>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="lcoe">
      <AccordionTrigger className="text-[12px] font-semibold">⚡ LCOE (Levelized Cost of Energy)</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <Box>
          <Formula>LCOE = Totale kosten (NCW) / Totale energie (NCW)</Formula>
          <p className="text-[10px]">De gemiddelde kosten per kWh over de hele projectlevensduur, verdisconteerd naar vandaag. Dit is de <strong>minimale verkoopprijs</strong> om break-even te draaien.</p>
          <p className="text-[10px] mt-1">Als het laadtarief (€/kWh) hoger is dan de LCOE → het project is winstgevend per kWh.</p>
        </Box>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="financing">
      <AccordionTrigger className="text-[12px] font-semibold">🏦 Financiering & Schuld</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <Box>
          <SectionTitle>Debt/Equity Split</SectionTitle>
          <p className="text-[10px]">De verhouding eigen vermogen (equity) vs. vreemd vermogen (debt). Default: <strong>70% schuld, 30% eigen vermogen</strong>.</p>
          <p className="text-[10px] mt-1">Meer schuld = hogere hefboomwerking (leverage), maar ook meer risico en hogere jaarlijkse lasten.</p>
        </Box>
        <Box>
          <SectionTitle>DSCR (Debt Service Coverage Ratio)</SectionTitle>
          <Formula>DSCR = Operationele cashflow / Jaarlijkse schuldaflossing</Formula>
          <p className="text-[10px]">Banken eisen typisch een minimum van <strong>1.2×</strong>. Onder 1.0× kan de schuld niet worden afgelost uit de operationele cashflow — dit is een deal-breaker voor financiering.</p>
        </Box>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
);

const BCGrafiekenTab = () => (
  <Accordion type="multiple" className="w-full space-y-1">
    {[
      ['cashflow', '💰 Cashflow Overzicht', 'Gestapelde bars per jaar met inkomsten (boven) en kosten (onder). De blauwe lijn toont de cumulatieve cashflow — wanneer die de x-as kruist, is de investering terugverdiend (= terugverdientijd).'],
      ['breakeven', '⚖️ Break-Even Analyse', 'Toont bij welk laadtarief de NPV precies €0 is. De rode verticale lijn is uw huidige tarief. Links van de break-even lijn = verliesgevend, rechts = winstgevend. Hoe verder rechts uw tarief, hoe robuuster het project.'],
      ['montecarlo', '🎲 Monte Carlo Simulatie', 'Voert 1.000 scenario\'s uit met willekeurige variatie op 6 parameters (laadtarief, PV CAPEX, BESS CAPEX, grid import, brandstof, inflatie). Het histogram toont de verdeling van NPV-uitkomsten. P10 = pessimistisch (90% kans op beter), P50 = mediaan, P90 = optimistisch.'],
      ['sensitivity', '🌪️ Gevoeligheidsanalyse (Tornado)', 'Tornado-diagram dat toont welke parameter de meeste impact heeft op de NPV bij ±20% wijziging. De langste balk = meest gevoelige parameter. Focus uw risicomanagement hier.'],
      ['dscr', '📊 DSCR per Jaar', 'Toont per jaar de Debt Service Coverage Ratio. De rode lijn op 1.2× is het bankminimum. Jaren onder de lijn zijn risicovolle jaren voor financiering.'],
      ['revenue', '📈 Omzetverdeling', 'Gestapelde bars per jaar: hoeveel omzet komt uit laden (primair), grid export (secundair) en SDE++ subsidie (tijdelijk, eerste 15 jaar).'],
      ['capex', '🏗️ CAPEX Waterval', 'Watervaldiagram dat de opbouw van de totale investering toont per component. Helpt te identificeren welke component de grootste kostenpost is.'],
      ['heatmap', '🗺️ NPV Heatmap', 'Matrix met NPV bij combinaties van twee parameters (bijv. laadtarief vs. PV-capaciteit). Groen = positieve NPV, rood = negatief. Helpt de "sweet spot" te vinden.'],
    ].map(([id, title, desc]) => (
      <AccordionItem key={id} value={id}>
        <AccordionTrigger className="text-[12px] font-semibold">{title}</AccordionTrigger>
        <AccordionContent className="text-[11px] text-muted-foreground leading-relaxed">
          <p>{desc}</p>
        </AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

const FiscaalTab = () => (
  <Accordion type="multiple" className="w-full space-y-1">
    <AccordionItem value="vpb">
      <AccordionTrigger className="text-[12px] font-semibold">🏛️ VPB (Vennootschapsbelasting)</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p>Tarief: <strong>25.8%</strong> over de fiscale winst (EBITDA − afschrijving − EIA-aftrek). Alleen geheven als de winst positief is.</p>
        <Box>
          <SectionTitle>Rekenvoorbeeld</SectionTitle>
          <Formula>EBITDA = €500.000</Formula>
          <Formula>Afschrijving = €200.000</Formula>
          <Formula>EIA-aftrek = €100.000</Formula>
          <Formula>Fiscale winst = €500.000 − €200.000 − €100.000 = €200.000</Formula>
          <Formula>VPB = €200.000 × 25.8% = €51.600</Formula>
        </Box>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="eia">
      <AccordionTrigger className="text-[12px] font-semibold">⚡ EIA (Energie-investeringsaftrek)</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p><strong>40%</strong> van de kwalificerende energie-investering mag extra worden afgetrokken van de fiscale winst. Dit verlaagt de effectieve investering.</p>
        <Box>
          <SectionTitle>Rekenvoorbeeld</SectionTitle>
          <Formula>Investering PV + BESS = €2.000.000 (kwalificeert voor EIA)</Formula>
          <Formula>EIA aftrek = €2.000.000 × 40% = €800.000 extra aftrek</Formula>
          <Formula>VPB-besparing = €800.000 × 25.8% = €206.400</Formula>
          <Formula>Effectief voordeel = ~10.3% van de investering</Formula>
        </Box>
        <Callout type="info">EIA moet binnen 3 maanden na opdrachtverstrekking worden aangemeld bij RVO. Kwalificerende investeringen staan op de <strong>Energielijst</strong> (jaarlijks geactualiseerd).</Callout>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="mia">
      <AccordionTrigger className="text-[12px] font-semibold">🌿 MIA (Milieu-investeringsaftrek)</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p>Aanvullende aftrek voor milieu-investeringen zoals laadinfrastructuur. Kan worden gecombineerd met EIA (maar niet op dezelfde investering).</p>
        <Box>
          <p className="text-[10px]">MIA-categorieën: I (45%), II (36%), III (27%). Laadinfra voor zwaar transport valt typisch in categorie I of II.</p>
        </Box>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="sde">
      <AccordionTrigger className="text-[12px] font-semibold">🌱 SDE++ Subsidie</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p>De <strong>Stimulering Duurzame Energieproductie en Klimaattransitie</strong> (SDE++) subsidie vergoedt het verschil tussen de kostprijs van hernieuwbare energie en de marktprijs.</p>
        <Box>
          <SectionTitle>Hoe het werkt</SectionTitle>
          <ul className="text-[10px] space-y-1 list-disc list-inside">
            <li>Subsidie per kWh opgewekte duurzame energie (PV)</li>
            <li>Looptijd: <strong>15 jaar</strong> vanaf ingebruikname</li>
            <li>Default in model: <strong>€0.05/kWh</strong></li>
            <li>Maximum aantal subsidiabele vollasturen per jaar</li>
          </ul>
        </Box>
        <Callout type="warning">SDE++ wordt jaarlijks opengesteld in rondes. Toekenning is niet gegarandeerd — houd hier rekening mee in risicoanalyses.</Callout>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="afschrijving">
      <AccordionTrigger className="text-[12px] font-semibold">📉 Afschrijving</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <p><strong>Lineaire afschrijving</strong> over de projectduur (standaard 20 jaar). De jaarlijkse afschrijving verlaagt de fiscale winst en daarmee de VPB-last.</p>
        <Box>
          <Formula>Jaarlijkse afschrijving = Totaal CAPEX / Projectduur</Formula>
          <Formula>Voorbeeld: €5.000.000 / 20 jaar = €250.000/jaar</Formula>
        </Box>
        <p>De restwaarde aan het eind van de projectduur wordt als €0 verondersteld (conservatieve aanname).</p>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
);

const AannamesTonTab = () => (
  <Accordion type="multiple" className="w-full space-y-1">
    <AccordionItem value="defaults">
      <AccordionTrigger className="text-[12px] font-semibold">📋 Alle default-waarden</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <table className="w-full text-[10px] border-collapse">
          <thead>
            <tr className="bg-muted">
              <th className="text-left px-2 py-1.5 border-b border-border text-muted-foreground">Parameter</th>
              <th className="text-right px-2 py-1.5 border-b border-border text-muted-foreground">Default</th>
              <th className="text-left px-2 py-1.5 border-b border-border text-muted-foreground">Bron</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['PV CAPEX', '€550/kWp', 'SolarPower Europe 2025, TNO'],
              ['BESS CAPEX', '€300/kWh', 'BloombergNEF BESS 2024, NREL'],
              ['Laadinfra', '€50.000/truck', 'McKinsey Electric Trucking 2024'],
              ['Backup CAPEX', '€400/kW', 'MAN/Caterpillar genset catalogi'],
              ['Netaansluiting', '€500.000', 'Liander/TenneT 2024-2025'],
              ['Engineering', '10%', 'Branchestandaard NL'],
              ['Grid import', '€0.12/kWh', 'APX Day-ahead NL 2024'],
              ['Grid export', '€0.06/kWh', 'APX minus profielverlies'],
              ['Blokstroom', '€0.08/kWh', 'Netbeheerder nacht-tarieven'],
              ['Transporttarief', '€45/kW/j', 'Liander/Stedin 2025'],
              ['Systeemdienstenkosten', '€2.500/j', 'Netbeheerder vast'],
              ['Meetkosten', '€800/j', 'Meetdiensten tarief'],
              ['Laadtarief', '€0.30/kWh', 'Marktconform publiek laden NL'],
              ['ERE tarief', '€0.13/kWh', 'RVO HBE/ERE regeling'],
              ['SDE++ subsidie', '€0.05/kWh', 'RVO basisbedragen 2024'],
              ['WACC', '6%', 'Damodaran — EU renewables'],
              ['Inflatie', '2.5%', 'ECB doelstelling, CPB'],
              ['VPB', '25.8%', 'Belastingdienst 2025'],
              ['EIA aftrek', '40%', 'RVO Energielijst 2026'],
              ['MIA aftrek', '27%', 'RVO MIA/Vamil 2026'],
              ['BESS degradatie', '2%/jaar', 'NREL, Lazard LCOS v8'],
              ['BESS rendement', '90%', 'Industriestandaard Li-ion'],
              ['BESS DoD', '90%', 'LFP-batterijen standaard'],
              ['Projectduur', '20 jaar', 'Energiesector standaard'],
            ].map(([param, val, bron], i) => (
              <tr key={i} className="border-b border-border/50">
                <td className="px-2 py-1 font-semibold text-foreground">{param}</td>
                <td className="px-2 py-1 text-right tabular-nums">{val}</td>
                <td className="px-2 py-1 text-[9px] text-muted-foreground">{bron}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="bronnen">
      <AccordionTrigger className="text-[12px] font-semibold">📚 Bronnenlijst</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-1.5 leading-relaxed">
        {[
          'BloombergNEF — Battery Energy Storage System Cost Survey 2024',
          'NREL — Grid-Scale Battery Storage Factsheet 2024',
          'Lazard — Levelized Cost of Storage Analysis v8 (2024)',
          'SolarPower Europe — Global Market Outlook 2024-2028',
          'TNO — Charging Infrastructure for Electric Trucks (2023)',
          'McKinsey — Electric Trucking: Path to Profitability (2024)',
          'Damodaran — Cost of Capital by Sector (EU Renewables, 2024)',
          'RVO — SDE++ Basisbedragen & EIA Energielijst (2026)',
          'Belastingdienst — VPB Tarief & EIA/MIA Regeling (2025-2026)',
          'KNMI — Uurlijkse instraling Nederland (referentiejaar)',
          'APX/EPEX SPOT — Day-ahead prijzen Nederland 2024',
          'Liander/TenneT — Aansluit- en transporttarieven 2024-2025',
        ].map((bron, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="text-muted-foreground shrink-0 text-[10px]">•</span>
            <span className="text-[10px]">{bron}</span>
          </div>
        ))}
      </AccordionContent>
    </AccordionItem>

    <AccordionItem value="disclaimer">
      <AccordionTrigger className="text-[12px] font-semibold">⚠️ Disclaimer & beperkingen</AccordionTrigger>
      <AccordionContent className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
        <Callout type="warning">
          <strong>Dit model is indicatief</strong> en niet geschikt voor definitieve investeringsbeslissingen zonder professionele validatie. Specifieke beperkingen:
        </Callout>
        <ul className="text-[10px] space-y-1 list-disc list-inside mt-2">
          <li>Zonprofiel is genormaliseerd — locatiespecifieke data kan afwijken</li>
          <li>Netcongestie wordt niet gemodelleerd (volledige beschikbaarheid aangenomen)</li>
          <li>Dynamische energieprijzen (APX spot) worden niet meegenomen</li>
          <li>BESS-degradatie is lineair — werkelijke degradatie kan non-lineair zijn</li>
          <li>Geen rekening met vergunningen, grondkosten of planologische beperkingen</li>
          <li>Subsidietoekenning (SDE++) is niet gegarandeerd</li>
          <li>Belastingtarieven en regelingen kunnen jaarlijks wijzigen</li>
        </ul>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
);

/* ─────────────── TABS CONFIG ─────────────── */
const TABS = [
  { id: 'overzicht', label: '🏠 Overzicht', Component: OverzichtTab },
  { id: 'params', label: '⚙️ Parameters', Component: ParametersTab },
  { id: 'simulatie', label: '🔬 Simulatie', Component: SimulatieTab },
  { id: 'resultaten', label: '📊 Resultaten', Component: ResultatenTab },
  { id: 'businesscase', label: '💰 Business Case', Component: BusinessCaseTab },
  { id: 'bcgrafieken', label: '📈 BC Grafieken', Component: BCGrafiekenTab },
  { id: 'fiscaal', label: '🏛️ Fiscaal', Component: FiscaalTab },
  { id: 'aannames', label: '📋 Aannames', Component: AannamesTonTab },
];

/* ─────────────── MAIN COMPONENT ─────────────── */
export default function FinancialMethodologyGuide({ onClose }: { onClose: () => void }) {
  const [activeTab, setActiveTab] = useState('overzicht');

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 print:hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div onClick={e => e.stopPropagation()} className="relative bg-background rounded-2xl max-w-[860px] w-full max-h-[85vh] flex flex-col shadow-2xl overflow-hidden border border-border">
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex justify-between items-center bg-primary">
          <div>
            <div className="text-base font-bold text-primary-foreground">📖 Methode & Handleiding</div>
            <div className="text-[10px] text-primary-foreground/70 mt-0.5">Simulatie & Business Case — Volledige documentatie</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadHubMethodologyMarkdown} className="bg-primary-foreground/20 border-none text-primary-foreground cursor-pointer text-[11px] px-3 py-1.5 rounded hover:bg-primary-foreground/30 transition-colors flex items-center gap-1.5" title="Download volledige methodologie als Markdown">📥 .md</button>
            <button onClick={onClose} className="bg-primary-foreground/20 border-none text-primary-foreground cursor-pointer text-base px-2 py-1 rounded leading-none hover:bg-primary-foreground/30 transition-colors">✕</button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="flex gap-0.5 px-3 py-2 overflow-x-auto bg-muted border-b border-border shrink-0 rounded-none justify-start h-auto">
            {TABS.map(t => (
              <TabsTrigger key={t.id} value={t.id} className="px-3 py-1.5 text-[10px] rounded-md whitespace-nowrap shrink-0">
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="p-5">
              {TABS.map(t => (
                <TabsContent key={t.id} value={t.id} className="mt-0">
                  <t.Component />
                </TabsContent>
              ))}
            </div>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
